'use client';

import { useEffect, useMemo, useState } from 'react';
import dynamic from 'next/dynamic';
import { useAppStore } from '@/stores/app-store';
import { DashWidget, Plant } from '@/lib/types';

/*
 * Smart Hotel SCADA — a self-contained demo for a city business hotel. No live
 * telemetry: every value is simulated in-widget (a smooth sine + jitter on a
 * frame counter) so it "lives" during a sales demo. Four tabs:
 *   - ผังระบบ (Plant SCADA): a mimic diagram — GRID/Solar → Main MDB →
 *     building riser, Chiller plant → CHW pumps → building, roof tank → building,
 *     with animated flow on power (gold) / chilled-water (cyan) / water (blue).
 *   - ห้องพัก (Rooms / GRMS): floor-plan grid, occupancy + AC + status.
 *   - Chiller/HVAC: central plant schematic (2 chillers, cooling tower, AHU).
 *   - พลังงาน & ESG: zone energy, solar, cost, carbon, kWh/occupied-room.
 *
 * Props follow the plant-scoped widget convention; `plant` is unused (simulated).
 */

interface Props { widget: DashWidget; plant: Plant; }

// home tab: KPI band + live 3D twin + status rail (three.js loads lazily inside it)
const HotelOverview = dynamic(() => import('./HotelCommandCenter'), { ssr: false });

type Tab = 'scada' | 'view3d' | 'rooms' | 'chiller' | 'energy';
type RoomState = 'occupied' | 'vacant' | 'cleaning' | 'checkout' | 'maint';

const FLOORS = 12;
const ROOMS_PER_FLOOR = 18;
const TOTAL_ROOMS = FLOORS * ROOMS_PER_FLOOR; // 216

const C = {
  txt: 'var(--text-primary)',
  mut: 'color-mix(in srgb, var(--text-muted) 55%, var(--text-primary))',
  gold: '#C9A227', green: '#10B77F', amber: '#F5A623', red: '#DC2626',
  blue: '#3B82F6', cyan: '#06B6D4', purple: '#8B5CF6', slate: '#64748B',
};
const BD = 'color-mix(in srgb, var(--text-muted) 40%, transparent)';
const PANEL = 'var(--bg-deep)';
const SURF = 'var(--bg-surface)';
const tnum = { fontVariantNumeric: 'tabular-nums' as const };

const ROOM_COLOR: Record<RoomState, string> = {
  occupied: C.green, vacant: C.slate, cleaning: C.amber, checkout: C.blue, maint: C.red,
};

function seeded(n: number): number {
  const x = Math.sin(n * 12.9898) * 43758.5453;
  return x - Math.floor(x);
}

export default function HotelScadaWidget({ }: Props) {
  const th = useAppStore((s) => s.lang) === 'th';
  const [tab, setTab] = useState<Tab>('view3d');
  const [frame, setFrame] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setFrame((f) => f + 1), 2500);
    return () => clearInterval(id);
  }, []);

  // pure in the frame, so the KPI sparklines can replay the last 24 ticks
  const waveAt = (f: number, base: number, amp: number, phase: number, period = 9) =>
    base + Math.sin((f + phase) / period) * amp + (seeded(f + phase) - 0.5) * amp * 0.3;
  const wave = (base: number, amp: number, phase: number, period = 9) => waveAt(frame, base, amp, phase, period);
  const hist = (fn: (f: number) => number) => Array.from({ length: 24 }, (_, i) => fn(frame - 23 + i));

  const rooms = useMemo(() => {
    const out: { id: string; floor: number; no: number; state: RoomState; setpoint: number }[] = [];
    for (let f = FLOORS; f >= 1; f--) {
      for (let n = 1; n <= ROOMS_PER_FLOOR; n++) {
        const s = seeded(f * 100 + n);
        let state: RoomState;
        if (s < 0.68) state = 'occupied';
        else if (s < 0.80) state = 'vacant';
        else if (s < 0.90) state = 'cleaning';
        else if (s < 0.96) state = 'checkout';
        else state = 'maint';
        out.push({ id: `${f}${String(n).padStart(2, '0')}`, floor: f, no: n, state, setpoint: 22 + Math.round(s * 3) });
      }
    }
    return out;
  }, []);

  const occupied = rooms.filter((r) => r.state === 'occupied').length;
  const occupancyPct = Math.round((occupied / TOTAL_ROOMS) * 100);
  const roomStates = useMemo(() => Array.from({ length: FLOORS }, (_, i) => rooms.filter((r) => r.floor === i + 1).sort((a, b) => a.no - b.no).map((r) => r.state as string)), [rooms]);

  const solarKw = Math.max(0, wave(38, 10, 0));
  const gridKw = wave(410, 40, 3);
  const totalKw = gridKw + solarKw;
  const chiller1 = { load: wave(74, 8, 1), chwS: wave(7.1, 0.4, 2), chwR: wave(12.3, 0.5, 4) };
  const chiller2 = { load: wave(61, 10, 5), chwS: wave(7.3, 0.4, 6), chwR: wave(12.0, 0.5, 3) };
  const chillerKw = wave(205, 22, 2);
  const coolingRT = wave(430, 30, 1);
  const kwPerRt = +(chillerKw / coolingRT).toFixed(2);
  const buildingKw = totalKw - chillerKw;
  const lobbyTemp = +wave(23.6, 0.5, 2).toFixed(1);
  const lobbyRh = Math.round(wave(54, 4, 5));
  const lobbyCo2 = Math.round(wave(620, 90, 3));
  const lobbyPm = Math.round(wave(18, 6, 7));
  const waterM3 = +wave(2.3, 0.5, 4).toFixed(1);
  const poolTemp = +wave(28.2, 0.3, 6).toFixed(1);
  const poolPh = +wave(7.4, 0.15, 2).toFixed(2);
  const tankPct = Math.round(wave(82, 5, 1));
  const chwSupply = +wave(7.2, 0.3, 2).toFixed(1);
  const cwSupply = +wave(31.8, 0.5, 3).toFixed(1);   // condenser water to chillers
  const cwReturn = +wave(36.9, 0.6, 5).toFixed(1);   // warm water back to cooling tower

  const energyToday = Math.round(wave(6850, 60, 0, 40));
  const solarToday = Math.round(wave(255, 8, 3, 40));
  const costToday = Math.round(energyToday * 4.2);
  const carbonToday = Math.round(energyToday * 0.42);
  const kwhPerOrn = +(energyToday / Math.max(1, occupied)).toFixed(1);
  const waterToday = Math.round(wave(148, 5, 2, 40));
  const roomCounts = { occupied, vacant: 0, cleaning: 0, checkout: 0, maint: 0 };
  for (const r of rooms) if (r.state !== 'occupied') roomCounts[r.state]++;
  const spark = {
    load: hist((f) => waveAt(f, 410, 40, 3) + Math.max(0, waveAt(f, 38, 10, 0))),
    energy: hist((f) => waveAt(f, 6850, 60, 0, 40)),
    eff: hist((f) => waveAt(f, 205, 22, 2) / waveAt(f, 430, 30, 1)),
    carbon: hist((f) => waveAt(f, 6850, 60, 0, 40) * 0.42),
    lobby: hist((f) => waveAt(f, 23.6, 0.5, 2)),
  };
  const comfortOk = lobbyTemp >= 22 && lobbyTemp <= 25 && lobbyCo2 < 900 && lobbyPm < 35;

  const zones = [
    { key: 'guest', th: 'ห้องพัก', en: 'Guest rooms', kw: wave(150, 12, 1), color: C.gold },
    { key: 'hvac', th: 'Chiller/HVAC', en: 'Chiller/HVAC', kw: chillerKw, color: C.cyan },
    { key: 'kitchen', th: 'ครัว/F&B', en: 'Kitchen/F&B', kw: wave(58, 10, 4), color: C.amber },
    { key: 'lobby', th: 'ล็อบบี้/ส่วนกลาง', en: 'Lobby/Common', kw: wave(42, 6, 2), color: C.blue },
    { key: 'laundry', th: 'ซักรีด', en: 'Laundry', kw: wave(24, 8, 6), color: C.purple },
    { key: 'pool', th: 'สระ/ฟิตเนส', en: 'Pool/Gym', kw: wave(18, 4, 3), color: C.green },
  ];
  const zoneTotal = zones.reduce((s, z) => s + z.kw, 0);

  const tabBtn = (id: Tab, label: string) => (
    <button key={id} onClick={() => setTab(id)}
      style={{ padding: '6px 13px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', borderRadius: 8, border: 'none',
        background: tab === id ? `color-mix(in srgb, ${C.gold} 16%, transparent)` : 'transparent',
        color: tab === id ? C.gold : C.mut }}>
      {label}
    </button>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, width: '100%', height: '100%', minHeight: 0, fontFamily: 'system-ui, sans-serif' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 16, fontWeight: 800, color: C.txt, letterSpacing: '0.01em' }}>🏨 {th ? 'สมาร์ทโฮเทล' : 'Smart Hotel'}</span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11.5, fontWeight: 700, color: C.green }}>
          <span style={{ width: 7, height: 7, borderRadius: 99, background: C.green, boxShadow: `0 0 0 3px color-mix(in srgb, ${C.green} 25%, transparent)` }} /> LIVE
        </span>
        <div style={{ display: 'flex', gap: 3, marginLeft: 'auto', flexWrap: 'wrap' }}>
          {tabBtn('view3d', th ? 'ภาพรวม' : 'Overview')}
          {tabBtn('scada', th ? 'ผังระบบ 2D' : 'Plant SCADA 2D')}
          {tabBtn('rooms', th ? 'ห้องพัก' : 'Rooms')}
          {tabBtn('chiller', th ? 'Chiller/HVAC' : 'Chiller/HVAC')}
          {tabBtn('energy', th ? 'พลังงาน & ESG' : 'Energy & ESG')}
        </div>
      </div>

      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {tab === 'scada' && (
          <Scada th={th} occupancyPct={occupancyPct} occupied={occupied} totalRooms={TOTAL_ROOMS}
            energyToday={energyToday} costToday={costToday} kwhPerOrn={kwhPerOrn} solarToday={solarToday}
            carbonToday={carbonToday} comfortOk={comfortOk} lobbyTemp={lobbyTemp} lobbyRh={lobbyRh}
            lobbyCo2={lobbyCo2} lobbyPm={lobbyPm} kwPerRt={kwPerRt} totalKw={totalKw} gridKw={gridKw}
            solarKw={solarKw} chillerKw={chillerKw} buildingKw={buildingKw} coolingRT={coolingRT}
            chwSupply={chwSupply} tankPct={tankPct} poolTemp={poolTemp} rooms={rooms} />
        )}
        {tab === 'view3d' && (
          <HotelOverview th={th} roomStates={roomStates} roomsPerFloor={ROOMS_PER_FLOOR} totalRooms={TOTAL_ROOMS} occupied={occupied}
            occupancyPct={occupancyPct} roomCounts={roomCounts} gridKw={gridKw} solarKw={solarKw} totalKw={totalKw} buildingKw={buildingKw}
            chillerKw={chillerKw} coolingRT={coolingRT} kwPerRt={kwPerRt} chwSupply={chwSupply} tankPct={tankPct} poolTemp={poolTemp}
            lobbyTemp={lobbyTemp} lobbyCo2={lobbyCo2} energyToday={energyToday} costToday={costToday} kwhPerOrn={kwhPerOrn}
            solarToday={solarToday} carbonToday={carbonToday} comfortOk={comfortOk} spark={spark} onOpenTab={setTab} />
        )}
        {tab === 'rooms' && <Rooms th={th} rooms={rooms} frame={frame} />}
        {tab === 'chiller' && <Chiller th={th} c1={chiller1} c2={chiller2} chillerKw={chillerKw} coolingRT={coolingRT} kwPerRt={kwPerRt} lobbyTemp={lobbyTemp} cwSupply={cwSupply} cwReturn={cwReturn} chwSupply={chwSupply} />}
        {tab === 'energy' && (
          <Energy th={th} zones={zones} zoneTotal={zoneTotal} energyToday={energyToday} solarToday={solarToday}
            costToday={costToday} carbonToday={carbonToday} kwhPerOrn={kwhPerOrn} waterToday={waterToday}
            poolTemp={poolTemp} poolPh={poolPh} waterM3={waterM3} solarKw={solarKw} frame={frame} />
        )}
      </div>
    </div>
  );
}

function Kpi({ label, value, unit, sub, color }: { label: string; value: string; unit?: string; sub?: string; color?: string }) {
  return (
    <div style={{ flex: 1, minWidth: 120, background: PANEL, border: `1px solid ${BD}`, borderRadius: 12, padding: '9px 12px' }}>
      <div style={{ fontSize: 9.5, color: C.mut, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 800, color: color || C.txt, ...tnum, marginTop: 2 }}>
        {value}{unit && <span style={{ fontSize: 11, fontWeight: 600, color: C.mut, marginLeft: 3 }}>{unit}</span>}
      </div>
      {sub && <div style={{ fontSize: 10, color: C.mut, marginTop: 1 }}>{sub}</div>}
    </div>
  );
}
function SectionTitle({ children }: { children: React.ReactNode }) {
  return <div style={{ fontSize: 12, fontWeight: 800, color: C.txt, margin: '2px 0 8px' }}>{children}</div>;
}

// ── PLANT SCADA (mimic diagram) ─────────────────────────────────────────────
function Scada(p: {
  th: boolean; occupancyPct: number; occupied: number; totalRooms: number;
  energyToday: number; costToday: number; kwhPerOrn: number; solarToday: number; carbonToday: number;
  comfortOk: boolean; lobbyTemp: number; lobbyRh: number; lobbyCo2: number; lobbyPm: number;
  kwPerRt: number; totalKw: number; gridKw: number; solarKw: number; chillerKw: number; buildingKw: number;
  coolingRT: number; chwSupply: number; tankPct: number; poolTemp: number;
  rooms: { floor: number; state: RoomState }[];
}) {
  const th = p.th;
  const VW = 1000, VH = 600;

  // node centers (viewBox units)
  const grid = { x: 110, y: 135 };
  const solar = { x: 110, y: 335 };
  const mdb = { x: 365, y: 235 };
  const chiller = { x: 360, y: 475 };
  const chwpump = { x: 600, y: 475 };
  const tank = { x: 600, y: 120 };
  const pool = { x: 600, y: 300 };
  const building = { x: 858, y: 315 };
  const BW = 250, BH = 470;

  // building left edge for line anchors
  const bL = building.x - BW / 2; // 733

  const perFloor = useMemo(() => {
    const m = new Map<number, { occ: number; total: number }>();
    for (const r of p.rooms) { const g = m.get(r.floor) || { occ: 0, total: 0 }; g.total++; if (r.state === 'occupied') g.occ++; m.set(r.floor, g); }
    return [...m.entries()].sort((a, b) => b[0] - a[0]);
  }, [p.rooms]);

  // orthogonal elbow flow line: faint base + animated marching dashes
  const line = (a: { x: number; y: number }, b: { x: number; y: number }, color: string, key: string) => {
    const midX = (a.x + b.x) / 2;
    const d = `M ${a.x} ${a.y} L ${midX} ${a.y} L ${midX} ${b.y} L ${b.x} ${b.y}`;
    return (
      <g key={key}>
        <path d={d} fill="none" stroke={color} strokeWidth={6} strokeOpacity={0.2} strokeLinecap="round" strokeLinejoin="round" />
        <path className="hx-flow" d={d} fill="none" stroke={color} strokeWidth={2.6} strokeLinecap="round" strokeLinejoin="round" />
      </g>
    );
  };

  const FS = { tag: 'clamp(7px,1.0cqw,10px)', title: 'clamp(8px,1.3cqw,13px)', val: 'clamp(11px,1.9cqw,18px)', unit: 'clamp(7px,1.0cqw,10px)', emoji: 'clamp(15px,3cqw,30px)' };

  const node = (c: { x: number; y: number }, w: number, h: number, icon: string, title: string, value: string, unit: string, color: string, sub?: string) => (
    <div style={{ position: 'absolute', left: `${(c.x / VW) * 100}%`, top: `${(c.y / VH) * 100}%`, width: `${(w / VW) * 100}%`, height: `${(h / VH) * 100}%`,
      transform: 'translate(-50%,-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1,
      background: PANEL, border: `1.8px solid ${color}`, borderRadius: 'clamp(8px,1.2cqw,13px)', boxShadow: '0 2px 10px rgba(0,0,0,0.12)', overflow: 'hidden', textAlign: 'center' }}>
      <span style={{ fontSize: FS.emoji, lineHeight: 1 }}>{icon}</span>
      <div style={{ fontSize: FS.title, fontWeight: 800, color: C.txt, whiteSpace: 'nowrap', maxWidth: '96%', overflow: 'hidden', textOverflow: 'ellipsis' }}>{title}</div>
      <div style={{ fontSize: FS.val, fontWeight: 800, color, lineHeight: 1.15, ...tnum }}>{value}<span style={{ fontSize: FS.unit, color: C.mut, fontWeight: 600, marginLeft: 2 }}>{unit}</span></div>
      {sub && <div style={{ fontSize: FS.tag, color: C.mut, whiteSpace: 'nowrap' }}>{sub}</div>}
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, containerType: 'inline-size' }}>
      <style>{`
        .hx-flow { stroke-dasharray: 8 10; animation: hxMove 1.1s linear infinite; }
        @keyframes hxMove { to { stroke-dashoffset: -18; } }
        @media (prefers-reduced-motion: reduce){ .hx-flow { animation: none; } }
      `}</style>

      {/* KPI strip */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Kpi label={th ? 'อัตราเข้าพัก' : 'Occupancy'} value={`${p.occupancyPct}`} unit="%" color={C.gold} sub={`${p.occupied}/${p.totalRooms}`} />
        <Kpi label={th ? 'พลังงานวันนี้' : 'Energy today'} value={p.energyToday.toLocaleString()} unit="kWh" sub={`฿${p.costToday.toLocaleString()}`} />
        <Kpi label={th ? 'พลังงาน/ห้อง' : 'kWh/room'} value={`${p.kwhPerOrn}`} unit="kWh" color={C.cyan} />
        <Kpi label={th ? 'โซลาร์วันนี้' : 'Solar today'} value={p.solarToday.toLocaleString()} unit="kWh" color={C.green} />
        <Kpi label={th ? 'คาร์บอน' : 'Carbon'} value={p.carbonToday.toLocaleString()} unit="kg" sub="CO₂e" />
        <Kpi label={th ? 'ความสบายแขก' : 'Comfort'} value={p.comfortOk ? (th ? 'ดี' : 'Good') : (th ? 'เฝ้าระวัง' : 'Watch')} color={p.comfortOk ? C.green : C.amber} sub={`${p.lobbyTemp}°C`} />
      </div>

      {/* mimic diagram */}
      <div style={{ position: 'relative', width: '100%', aspectRatio: `${VW} / ${VH}`, background: SURF, border: `1px solid ${BD}`, borderRadius: 14, overflow: 'hidden', containerType: 'inline-size' }}>
        <svg viewBox={`0 0 ${VW} ${VH}`} preserveAspectRatio="none" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', display: 'block' }}>
          {/* power (gold) */}
          {line({ x: grid.x + 75, y: grid.y }, { x: mdb.x - 90, y: mdb.y - 25 }, C.gold, 'g-m')}
          {line({ x: solar.x + 75, y: solar.y }, { x: mdb.x - 90, y: mdb.y + 25 }, C.green, 's-m')}
          {line({ x: mdb.x + 90, y: mdb.y + 20 }, { x: chiller.x, y: chiller.y - 60 }, C.gold, 'm-ch')}
          {line({ x: mdb.x + 90, y: mdb.y }, { x: pool.x - 60, y: pool.y }, C.gold, 'm-pool')}
          {line({ x: mdb.x + 90, y: mdb.y - 20 }, { x: bL, y: building.y - 90 }, C.gold, 'm-b')}
          {/* chilled water (cyan) */}
          {line({ x: chiller.x + 85, y: chiller.y }, { x: chwpump.x - 60, y: chwpump.y }, C.cyan, 'ch-p')}
          {line({ x: chwpump.x + 60, y: chwpump.y }, { x: bL, y: building.y + 40 }, C.cyan, 'p-b')}
          {/* water (blue) */}
          {line({ x: tank.x + 75, y: tank.y }, { x: bL, y: building.y - 150 }, C.blue, 't-b')}
        </svg>

        {/* nodes */}
        {node(grid, 150, 110, '🗼', th ? 'การไฟฟ้า' : 'Grid', `${Math.round(p.gridKw)}`, 'kW', C.gold)}
        {node(solar, 150, 110, '☀️', th ? 'โซลาร์ 50kWp' : 'Solar 50kWp', `${Math.round(p.solarKw)}`, 'kW', C.green)}
        {node(mdb, 180, 150, '⚡', th ? 'MDB หลัก' : 'Main MDB', `${Math.round(p.totalKw)}`, 'kW', C.gold, th ? 'สวิตช์บอร์ด LV' : 'LV switchboard')}
        {node(chiller, 170, 120, '❄️', th ? 'ชิลเลอร์' : 'Chiller plant', `${Math.round(p.coolingRT)}`, 'RT', C.cyan, `${Math.round(p.chillerKw)}kW · ${p.kwPerRt}kW/RT`)}
        {node(chwpump, 120, 100, '🔵', th ? 'ปั๊มน้ำเย็น' : 'CHW pumps', `${p.chwSupply}`, '°C', C.cyan)}
        {node(tank, 150, 105, '💧', th ? 'ถังน้ำดาดฟ้า' : 'Roof tank', `${p.tankPct}`, '%', C.blue)}
        {node(pool, 120, 100, '🏊', th ? 'สระ/ฟิตเนส' : 'Pool/Gym', `${p.poolTemp}`, '°C', C.blue)}

        {/* building riser (guest floors) */}
        <div style={{ position: 'absolute', left: `${(building.x / VW) * 100}%`, top: `${(building.y / VH) * 100}%`, width: `${(BW / VW) * 100}%`, height: `${(BH / VH) * 100}%`,
          transform: 'translate(-50%,-50%)', background: PANEL, border: `1.8px solid ${C.gold}`, borderRadius: 'clamp(8px,1.2cqw,13px)', boxShadow: '0 2px 12px rgba(0,0,0,0.14)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '5px 8px', background: `linear-gradient(90deg, color-mix(in srgb, ${C.gold} 20%, transparent), transparent)`, display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 'clamp(13px,2.4cqw,20px)' }}>🏨</span>
            <div>
              <div style={{ fontSize: FS.title, fontWeight: 800, color: C.txt, whiteSpace: 'nowrap' }}>{th ? 'อาคารห้องพัก' : 'Guest tower'}</div>
              <div style={{ fontSize: FS.tag, color: C.mut, whiteSpace: 'nowrap' }}>{p.occupied}/{p.totalRooms} · {Math.round(p.buildingKw)}kW · {p.lobbyTemp}°C</div>
            </div>
          </div>
          <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 1, padding: '3px 6px', overflow: 'hidden' }}>
            {perFloor.map(([f, g]) => {
              const pct = Math.round((g.occ / g.total) * 100);
              return (
                <div key={f} style={{ display: 'flex', alignItems: 'center', gap: 4, flex: 1, minHeight: 0 }}>
                  <span style={{ width: '13%', fontSize: FS.tag, fontWeight: 700, color: C.mut, ...tnum }}>F{f}</span>
                  <div style={{ flex: 1, height: '62%', background: 'color-mix(in srgb, var(--text-muted) 14%, transparent)', borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{ width: `${pct}%`, height: '100%', background: `color-mix(in srgb, ${C.gold} ${40 + pct / 2}%, ${C.green})`, borderRadius: 3 }} />
                  </div>
                  <span style={{ width: '16%', textAlign: 'right', fontSize: FS.tag, color: C.mut, ...tnum }}>{pct}%</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* legend */}
        <div style={{ position: 'absolute', left: 8, bottom: 6, display: 'flex', gap: 10, fontSize: 'clamp(7px,1.0cqw,10px)', color: C.mut, fontWeight: 700 }}>
          <span><span style={{ display: 'inline-block', width: 10, height: 3, background: C.gold, verticalAlign: 'middle', marginRight: 3 }} />{th ? 'ไฟฟ้า' : 'Power'}</span>
          <span><span style={{ display: 'inline-block', width: 10, height: 3, background: C.cyan, verticalAlign: 'middle', marginRight: 3 }} />{th ? 'น้ำเย็น' : 'Chilled water'}</span>
          <span><span style={{ display: 'inline-block', width: 10, height: 3, background: C.blue, verticalAlign: 'middle', marginRight: 3 }} />{th ? 'น้ำใช้' : 'Water'}</span>
        </div>
      </div>
    </div>
  );
}

// ── ROOMS (GRMS) ────────────────────────────────────────────────────────────
function Rooms(p: { th: boolean; rooms: { id: string; floor: number; no: number; state: RoomState; setpoint: number }[]; frame: number }) {
  const th = p.th;
  const [sel, setSel] = useState<string | null>(null);
  const [filter, setFilter] = useState<RoomState | 'all'>('all');

  const LABEL: Record<RoomState, string> = {
    occupied: th ? 'มีแขก' : 'Occupied', vacant: th ? 'ว่าง' : 'Vacant',
    cleaning: th ? 'ทำความสะอาด' : 'Cleaning', checkout: th ? 'เช็คเอาท์' : 'Checkout', maint: th ? 'ซ่อมบำรุง' : 'Maintenance',
  };
  const counts = useMemo(() => {
    const c: Record<RoomState, number> = { occupied: 0, vacant: 0, cleaning: 0, checkout: 0, maint: 0 };
    for (const r of p.rooms) c[r.state]++;
    return c;
  }, [p.rooms]);
  const byFloor = useMemo(() => {
    const m = new Map<number, typeof p.rooms>();
    for (const r of p.rooms) { const a = m.get(r.floor) || []; a.push(r); m.set(r.floor, a); }
    return [...m.entries()].sort((a, b) => b[0] - a[0]);
  }, [p.rooms]);

  const selRoom = p.rooms.find((r) => r.id === sel);
  const roomTemp = (r: { id: string; setpoint: number }) => +(r.setpoint + Math.sin((p.frame + r.id.length) / 6) * 0.6).toFixed(1);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', alignItems: 'center' }}>
        <button onClick={() => setFilter('all')} style={legendBtn(filter === 'all', C.slate)}>{th ? 'ทั้งหมด' : 'All'} {p.rooms.length}</button>
        {(Object.keys(LABEL) as RoomState[]).map((s) => (
          <button key={s} onClick={() => setFilter(filter === s ? 'all' : s)} style={legendBtn(filter === s, ROOM_COLOR[s])}>
            <span style={{ width: 9, height: 9, borderRadius: 3, background: ROOM_COLOR[s], display: 'inline-block', marginRight: 5 }} />
            {LABEL[s]} {counts[s]}
          </button>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: sel ? 'minmax(0,1fr) 230px' : '1fr', gap: 12 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, background: PANEL, border: `1px solid ${BD}`, borderRadius: 12, padding: 10 }}>
          {byFloor.map(([f, list]) => (
            <div key={f} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ width: 28, fontSize: 10, fontWeight: 800, color: C.mut, ...tnum }}>F{f}</span>
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {list.map((r) => {
                  const dim = filter !== 'all' && r.state !== filter;
                  return (
                    <button key={r.id} onClick={() => setSel(r.id)} title={`${r.id} · ${LABEL[r.state]}`}
                      style={{ width: 30, height: 22, borderRadius: 5, cursor: 'pointer', fontSize: 8.5, fontWeight: 700, ...tnum,
                        border: sel === r.id ? `2px solid ${C.txt}` : `1px solid color-mix(in srgb, ${ROOM_COLOR[r.state]} 55%, transparent)`,
                        background: `color-mix(in srgb, ${ROOM_COLOR[r.state]} ${r.state === 'vacant' ? 12 : 22}%, transparent)`,
                        color: C.txt, opacity: dim ? 0.22 : 1, transition: 'opacity .12s' }}>
                      {r.no}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        {selRoom && (
          <div style={{ background: PANEL, border: `1px solid ${BD}`, borderRadius: 12, padding: 14, height: 'fit-content' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 17, fontWeight: 800, color: C.txt }}>{th ? 'ห้อง' : 'Room'} {selRoom.id}</span>
              <button onClick={() => setSel(null)} style={{ border: 'none', background: 'transparent', color: C.mut, cursor: 'pointer', fontSize: 14 }}>✕</button>
            </div>
            <span style={{ display: 'inline-block', marginTop: 6, fontSize: 11, fontWeight: 800, color: ROOM_COLOR[selRoom.state], background: `color-mix(in srgb, ${ROOM_COLOR[selRoom.state]} 15%, transparent)`, padding: '3px 10px', borderRadius: 99 }}>{LABEL[selRoom.state]}</span>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 9, marginTop: 12 }}>
              {roomRow(th ? 'อุณหภูมิห้อง' : 'Room temp', `${roomTemp(selRoom)} °C`, C.amber)}
              {roomRow(th ? 'ตั้งแอร์' : 'AC setpoint', `${selRoom.setpoint} °C`, C.cyan)}
              {roomRow(th ? 'คีย์การ์ด' : 'Key-card', selRoom.state === 'occupied' ? (th ? 'เสียบอยู่' : 'Inserted') : (th ? 'ถอด' : 'Removed'), selRoom.state === 'occupied' ? C.green : C.slate)}
              {roomRow(th ? 'ไฟ' : 'Lights', selRoom.state === 'occupied' ? 'ON' : 'OFF', selRoom.state === 'occupied' ? C.green : C.slate)}
              {roomRow(th ? 'มินิบาร์' : 'Minibar', selRoom.state === 'checkout' ? (th ? 'ตรวจสอบ' : 'Check') : 'OK', selRoom.state === 'checkout' ? C.amber : C.green)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
function roomRow(label: string, val: string, color: string) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
      <span style={{ color: C.mut, fontWeight: 600 }}>{label}</span>
      <span style={{ marginLeft: 'auto', fontWeight: 800, color, ...tnum }}>{val}</span>
    </div>
  );
}
function legendBtn(active: boolean, color: string): React.CSSProperties {
  return { padding: '4px 10px', fontSize: 11, fontWeight: 700, borderRadius: 99, cursor: 'pointer',
    border: `1px solid ${active ? color : BD}`, background: active ? `color-mix(in srgb, ${color} 14%, transparent)` : 'transparent', color: C.txt };
}

// ── CHILLER / HVAC (SCADA mimic) ────────────────────────────────────────────
function Chiller(p: {
  th: boolean; c1: { load: number; chwS: number; chwR: number }; c2: { load: number; chwS: number; chwR: number };
  chillerKw: number; coolingRT: number; kwPerRt: number; lobbyTemp: number;
  cwSupply: number; cwReturn: number; chwSupply: number;
}) {
  const th = p.th;
  const VW = 1000, VH = 460;

  // condenser-water pipe (warm loop, amber) + chilled-water pipe (cold loop, cyan)
  const pipe = (d: string, color: string, key: string) => (
    <g key={key}>
      <path d={d} fill="none" stroke={color} strokeWidth={7} strokeOpacity={0.2} strokeLinecap="round" strokeLinejoin="round" />
      <path className="hx-flow" d={d} fill="none" stroke={color} strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" />
    </g>
  );

  const FS = { tag: 'clamp(7px,1.0cqw,10px)', title: 'clamp(8px,1.35cqw,13px)', val: 'clamp(11px,2.0cqw,19px)', unit: 'clamp(7px,1.0cqw,10px)', emoji: 'clamp(15px,3.2cqw,32px)' };

  const node = (c: { x: number; y: number }, w: number, h: number, body: React.ReactNode, color: string, on = true) => (
    <div style={{ position: 'absolute', left: `${(c.x / VW) * 100}%`, top: `${(c.y / VH) * 100}%`, width: `${(w / VW) * 100}%`, height: `${(h / VH) * 100}%`,
      transform: 'translate(-50%,-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1,
      background: PANEL, border: `1.8px solid ${on ? color : BD}`, borderRadius: 'clamp(8px,1.2cqw,13px)', boxShadow: '0 2px 10px rgba(0,0,0,0.12)', overflow: 'hidden', textAlign: 'center', padding: '2px 4px' }}>
      {body}
    </div>
  );
  const label = (t: string) => <div style={{ fontSize: FS.title, fontWeight: 800, color: C.txt, whiteSpace: 'nowrap' }}>{t}</div>;
  const val = (v: string, unit: string, color: string) => <div style={{ fontSize: FS.val, fontWeight: 800, color, lineHeight: 1.1, ...tnum }}>{v}<span style={{ fontSize: FS.unit, color: C.mut, fontWeight: 600, marginLeft: 2 }}>{unit}</span></div>;
  const tag = (t: string) => <div style={{ fontSize: FS.tag, color: C.mut, whiteSpace: 'nowrap' }}>{t}</div>;

  const chillerNode = (c: { x: number; y: number }, name: string, ch: { load: number; chwS: number; chwR: number }) => (
    <div style={{ position: 'absolute', left: `${(c.x / VW) * 100}%`, top: `${(c.y / VH) * 100}%`, width: `${(190 / VW) * 100}%`, height: `${(130 / VH) * 100}%`,
      transform: 'translate(-50%,-50%)', background: PANEL, border: `1.8px solid ${C.cyan}`, borderRadius: 'clamp(8px,1.2cqw,13px)', boxShadow: '0 2px 10px rgba(0,0,0,0.12)', overflow: 'hidden', display: 'flex', flexDirection: 'column', padding: '5px 8px', gap: 3 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
        <span style={{ fontSize: FS.title }}>❄️</span>
        <span style={{ fontSize: FS.title, fontWeight: 800, color: C.txt, whiteSpace: 'nowrap' }}>{name}</span>
        <span style={{ marginLeft: 'auto', width: 7, height: 7, borderRadius: 99, background: C.green, boxShadow: `0 0 5px ${C.green}` }} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: FS.tag, color: C.mut, fontWeight: 700 }}><span>{th ? 'โหลด' : 'Load'}</span><span style={tnum}>{Math.round(ch.load)}%</span></div>
      <div style={{ height: 7, background: 'color-mix(in srgb, var(--text-muted) 14%, transparent)', borderRadius: 99, overflow: 'hidden' }}>
        <div style={{ width: `${Math.round(ch.load)}%`, height: '100%', background: C.cyan }} />
      </div>
      <div style={{ display: 'flex', gap: 5, marginTop: 'auto' }}>
        <div style={{ flex: 1, textAlign: 'center', background: SURF, borderRadius: 6, padding: '2px 0' }}>
          <div style={{ fontSize: FS.tag, color: C.mut, fontWeight: 700 }}>CHW {th ? 'จ่าย' : 'out'}</div>
          <div style={{ fontSize: FS.title, fontWeight: 800, color: C.blue, ...tnum }}>{ch.chwS.toFixed(1)}°</div>
        </div>
        <div style={{ flex: 1, textAlign: 'center', background: SURF, borderRadius: 6, padding: '2px 0' }}>
          <div style={{ fontSize: FS.tag, color: C.mut, fontWeight: 700 }}>CHW {th ? 'กลับ' : 'in'}</div>
          <div style={{ fontSize: FS.title, fontWeight: 800, color: C.amber, ...tnum }}>{ch.chwR.toFixed(1)}°</div>
        </div>
      </div>
    </div>
  );

  // node centers
  const ct = { x: 108, y: 230 };
  const cwp = { x: 285, y: 230 };
  const ch1 = { x: 495, y: 140 };
  const ch2 = { x: 495, y: 330 };
  const chwp = { x: 712, y: 235 };
  const bldg = { x: 895, y: 235 };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, containerType: 'inline-size' }}>
      <style>{`
        .hx-flow { stroke-dasharray: 8 10; animation: hxMove 1.1s linear infinite; }
        @keyframes hxMove { to { stroke-dashoffset: -18; } }
        @media (prefers-reduced-motion: reduce){ .hx-flow { animation: none; } }
      `}</style>

      <div style={{ display: 'flex', gap: 9, flexWrap: 'wrap' }}>
        <Kpi label={th ? 'ภาระทำความเย็น' : 'Cooling load'} value={`${Math.round(p.coolingRT)}`} unit="RT" color={C.cyan} />
        <Kpi label={th ? 'กำลังไฟชิลเลอร์' : 'Chiller power'} value={`${Math.round(p.chillerKw)}`} unit="kW" />
        <Kpi label={th ? 'ประสิทธิภาพ' : 'Efficiency'} value={`${p.kwPerRt}`} unit="kW/RT" color={p.kwPerRt < 0.6 ? C.green : p.kwPerRt < 0.75 ? C.amber : C.red} sub={p.kwPerRt < 0.65 ? (th ? 'ดีมาก' : 'Excellent') : (th ? 'ปกติ' : 'Normal')} />
        <Kpi label={th ? 'อุณหภูมิพื้นที่' : 'Space temp'} value={`${p.lobbyTemp}`} unit="°C" color={C.amber} />
      </div>

      {/* mimic */}
      <div style={{ position: 'relative', width: '100%', aspectRatio: `${VW} / ${VH}`, background: SURF, border: `1px solid ${BD}`, borderRadius: 14, overflow: 'hidden', containerType: 'inline-size' }}>
        <svg viewBox={`0 0 ${VW} ${VH}`} preserveAspectRatio="none" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', display: 'block' }}>
          {/* condenser water (amber): CT → CW pump → chillers manifold */}
          {pipe('M 183 230 L 237 230', C.amber, 'ct-p')}
          {pipe('M 333 230 L 380 230 L 380 140 L 400 140', C.amber, 'p-c1')}
          {pipe('M 380 230 L 380 330 L 400 330', C.amber, 'p-c2')}
          {/* chilled water (cyan): chillers manifold → CHW pump → building */}
          {pipe('M 590 140 L 610 140 L 610 235 L 664 235', C.cyan, 'c1-p')}
          {pipe('M 590 330 L 610 330 L 610 235', C.cyan, 'c2-p')}
          {pipe('M 760 235 L 820 235', C.cyan, 'p-b')}
        </svg>

        {node(ct, 150, 130, <>
          <span style={{ fontSize: FS.emoji }}>🌀</span>{label(th ? 'คูลลิ่งทาวเวอร์' : 'Cooling tower')}
          {tag(`${th ? 'จ่าย' : 'CW'} ${p.cwSupply}° · ${th ? 'กลับ' : 'ret'} ${p.cwReturn}°`)}
        </>, C.amber)}
        {node(cwp, 96, 82, <><span style={{ fontSize: FS.emoji }}>🔵</span>{label(th ? 'ปั๊ม CW' : 'CW pump')}{tag('ON')}</>, C.amber)}
        {chillerNode(ch1, th ? 'ชิลเลอร์ 1' : 'Chiller 1', p.c1)}
        {chillerNode(ch2, th ? 'ชิลเลอร์ 2' : 'Chiller 2', p.c2)}
        {node(chwp, 96, 82, <><span style={{ fontSize: FS.emoji }}>🔵</span>{label(th ? 'ปั๊ม CHW' : 'CHW pump')}{val(`${p.chwSupply}`, '°C', C.blue)}</>, C.cyan)}
        {node(bldg, 150, 150, <>
          <span style={{ fontSize: FS.emoji }}>🏢</span>{label(th ? 'อาคาร (AHU/FCU)' : 'Building AHU/FCU')}
          {val(`${Math.round(p.coolingRT)}`, 'RT', C.cyan)}{tag(`${p.lobbyTemp}°C`)}
        </>, C.cyan)}

        {/* legend */}
        <div style={{ position: 'absolute', left: 8, bottom: 6, display: 'flex', gap: 10, fontSize: 'clamp(7px,1.0cqw,10px)', color: C.mut, fontWeight: 700 }}>
          <span><span style={{ display: 'inline-block', width: 10, height: 3, background: C.amber, verticalAlign: 'middle', marginRight: 3 }} />{th ? 'น้ำระบายความร้อน (CW)' : 'Condenser water'}</span>
          <span><span style={{ display: 'inline-block', width: 10, height: 3, background: C.cyan, verticalAlign: 'middle', marginRight: 3 }} />{th ? 'น้ำเย็น (CHW)' : 'Chilled water'}</span>
        </div>
      </div>
    </div>
  );
}

// ── ENERGY & ESG ────────────────────────────────────────────────────────────
function Energy(p: {
  th: boolean; zones: { key: string; th: string; en: string; kw: number; color: string }[]; zoneTotal: number;
  energyToday: number; solarToday: number; costToday: number; carbonToday: number; kwhPerOrn: number; waterToday: number;
  poolTemp: number; poolPh: number; waterM3: number; solarKw: number; frame: number;
}) {
  const th = p.th;
  const spark = useMemo(() => {
    const pts: number[] = [];
    for (let h = 0; h < 24; h++) {
      const day = 0.55 + 0.45 * Math.sin(((h - 7) / 24) * Math.PI * 2);
      pts.push(300 + day * 180 + (seeded(h + p.frame) - 0.5) * 20);
    }
    return pts;
  }, [p.frame]);
  const max = Math.max(...spark), min = Math.min(...spark);
  const path = spark.map((v, i) => `${(i / 23) * 100},${100 - ((v - min) / (max - min || 1)) * 100}`).join(' ');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', gap: 9, flexWrap: 'wrap' }}>
        <Kpi label={th ? 'พลังงานวันนี้' : 'Energy today'} value={p.energyToday.toLocaleString()} unit="kWh" sub={`฿${p.costToday.toLocaleString()}`} />
        <Kpi label={th ? 'พลังงาน/ห้องพัก' : 'kWh / occ. room'} value={`${p.kwhPerOrn}`} unit="kWh" color={C.cyan} />
        <Kpi label={th ? 'โซลาร์วันนี้' : 'Solar today'} value={p.solarToday.toLocaleString()} unit="kWh" color={C.green} sub={`฿${(p.solarToday * 4.2).toLocaleString()} ${th ? 'ประหยัด' : 'saved'}`} />
        <Kpi label={th ? 'คาร์บอนวันนี้' : 'Carbon today'} value={p.carbonToday.toLocaleString()} unit="kg" sub="CO₂e" />
        <Kpi label={th ? 'น้ำวันนี้' : 'Water today'} value={`${p.waterToday}`} unit="m³" color={C.blue} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 12 }}>
        <div style={{ background: PANEL, border: `1px solid ${BD}`, borderRadius: 12, padding: 13 }}>
          <SectionTitle>{th ? 'พลังงานตามโซน (ตอนนี้)' : 'Energy by zone (now)'}</SectionTitle>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
            {p.zones.map((z) => {
              const pct = Math.round((z.kw / p.zoneTotal) * 100);
              return (
                <div key={z.key} style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                  <span style={{ width: 108, fontSize: 11, color: C.txt, fontWeight: 600 }}>{th ? z.th : z.en}</span>
                  <div style={{ flex: 1, height: 12, background: 'color-mix(in srgb, var(--text-muted) 14%, transparent)', borderRadius: 99, overflow: 'hidden' }}>
                    <div style={{ width: `${pct}%`, height: '100%', background: z.color, borderRadius: 99 }} />
                  </div>
                  <span style={{ width: 74, textAlign: 'right', fontSize: 11, fontWeight: 800, color: z.color, ...tnum }}>{Math.round(z.kw)}kW · {pct}%</span>
                </div>
              );
            })}
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ background: PANEL, border: `1px solid ${BD}`, borderRadius: 12, padding: 13 }}>
            <SectionTitle>{th ? 'โหลดไฟ 24 ชม.' : 'Load — last 24h'}</SectionTitle>
            <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{ width: '100%', height: 90 }}>
              <polyline points={path} fill="none" stroke={C.gold} strokeWidth="1.6" vectorEffect="non-scaling-stroke" />
            </svg>
          </div>
          <div style={{ background: PANEL, border: `1px solid ${BD}`, borderRadius: 12, padding: 13 }}>
            <SectionTitle>{th ? 'สระว่ายน้ำ & น้ำ' : 'Pool & water'}</SectionTitle>
            <div style={{ display: 'flex', gap: 8 }}>
              {[[th ? 'อุณหภูมิสระ' : 'Pool temp', `${p.poolTemp}°C`, C.cyan], ['pH', `${p.poolPh}`, p.poolPh >= 7.2 && p.poolPh <= 7.6 ? C.green : C.amber], [th ? 'น้ำ (ตอนนี้)' : 'Water now', `${p.waterM3} m³/h`, C.blue]].map(([l, v, c], i) => (
                <div key={i} style={{ flex: 1, textAlign: 'center', background: SURF, borderRadius: 8, padding: '8px 4px' }}>
                  <div style={{ fontSize: 9.5, color: C.mut, fontWeight: 700 }}>{l as string}</div>
                  <div style={{ fontSize: 15, fontWeight: 800, color: c as string, ...tnum }}>{v as string}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
